import { Alert } from '@mui/material';
import * as Sentry from '@sentry/react';
import Button from '_atoms/Button/Button';
import FormControl from '_atoms/Form/Control';
import InputBox from '_atoms/InputBox';
import Tree from '_atoms/Tree';
import { TranslatableString } from '_i18n/no/translations';
import UserApi from '_services/Api/User';
import { flashMessageSlice } from '_services/Redux/FlashMessage/FlashMessageSlice';
import useNavigate from 'Hooks/useNavigate';
import GuestLayout from 'Pages/_layout/Guest';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import './style.scss';

type Errors = { error?: string; email?: string };

const ForgotPasswordPage = (): React.JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { sayError, saySuccess } = flashMessageSlice.actions;
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    /**
     * The state of this functional component is used to store the email and password from the form.
     * These are passed further to the post request for login
     */
    const [state, setState] = useState({ email: '' });

    /**
     * Holds the errors of the form or request.
     * These are populated with inner validation or api request errors
     *
     * @see isValid()
     * @see handleSubmit()
     */
    const [errors, setErrors] = useState<Errors>({});
    /**
     * Checks whether the login form is valid. Sets errors to the state if it's not.
     *
     * @returns boolean
     */
    const isValid = () => {
        /**
         * Contains the validation of the email and password inputs + error messages
         */
        const errors: Errors = {
            email: state.email.length
                ? ''
                : t('Email') + '' + t('is a required field'),
        };

        if (state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            errors.email =
                `"${state.email}" ` + t('is not a valid email address');
        }

        /**
         * Checks whether the form is valid by confirming that each propriety in the errors object doesn't contain any error message
         */
        const valid = Object.values(errors).every((error) => error === '');
        if (!valid) {
            /**
             * Updates the state with validation errors if there are any
             */
            setErrors(errors);
        }
        return valid;
    };
    /**
     * Handles all changes in the form by Input target.
     * Updates the state with propriety of the input id and the value from the input value
     *
     * @param event React.ChangeEvent<HTMLInputElement>
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;

        if (event.target.id === 'email') {
            value = value.replace(/\s/g, '');
        }

        setState({ ...state, [event.target.id]: value });
    };

    /**
     * Handles the submit of the form. If internal validation passes, the submit is furthered to the API
     *
     * @param event React.FormEvent<HTMLFormElement>
     * @returns void
     */
    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!isValid()) {
            /**
             * Stops further execution if the inputs are not valid
             */
            return;
        }

        /**
         * Using the state as post data.
         * @see state
         */
        const postData = state;
        setIsLoading(true);

        /**
         * Login is called on the api if cookie is received
         */
        UserApi.forgotPassword(postData)
            /**
             * When api call is done, use login action that dispatches the user to redux
             */
            .then((response) => {
                dispatch(
                    saySuccess(t(response.data.success as TranslatableString)),
                );
                navigate('/loginfallback', {
                    state: {
                        email: state.email,
                    },
                });
            })
            /**
             * If any errors happen during login, they should be handled here
             */
            .catch((error) => {
                if (error.response?.status === 422) {
                    const errorData: Errors = {
                        error: t(error.response.data.error),
                    };
                    setErrors(errorData);
                } else if (error.response?.status === 419) {
                    // Handle CSRF token mismatch specifically
                    const errorData: Errors = {
                        error: t(
                            'Your session has expired. Please refresh the page and try again.',
                        ),
                    };
                    setErrors(errorData);
                } else {
                    console.error(
                        'Forgot password error. Error:',
                        error.response,
                    );
                    Sentry.captureException(error);
                    /**
                     * If it ends up here, then we don't really know what happened
                     */
                    dispatch(sayError('An unexpected error happened.'));
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }
    return (
        <GuestLayout showImage={false} showClose={false}>
            <div className="forgot-password-page">
                <h1 data-cy="forgot-pass-title" className="title">
                    {t('Forgot password')}
                </h1>
                <div className="logo-wrapper">
                    <Tree />
                </div>
                <p className="content">
                    {t(
                        'Enter your email address and we will send you a link to reset your password',
                    )}
                </p>

                <form onSubmit={handleSubmit} className="forgot-password-form">
                    {errors.error && (
                        <Alert className="form-control" severity="error">
                            {errors.error}
                        </Alert>
                    )}
                    <FormControl>
                        <InputBox
                            value={state.email}
                            type="email"
                            id="email"
                            label="Email"
                            handleChange={handleChange}
                            placeholder="Email"
                            error={errors.email}
                            testId="email"
                        />
                    </FormControl>
                    <FormControl>
                        <Button
                            classes={!state.email ? 'disabled' : ''}
                            isDisabled={!state.email}
                            testId="forgot-pass-btn"
                            isLoading={isLoading}
                        >
                            Send
                        </Button>
                    </FormControl>
                </form>
            </div>
        </GuestLayout>
    );
};

export default ForgotPasswordPage;
